import * as Sentry from '@sentry/browser'
import {
  addOperationNameToBreadcrumbs,
  shouldIgnoreError,
  stripPiiDataFromPayload
} from './helpers'

type SentryCofigProps = {
  environment: string // "checkout" | "team-controls" | "development" | "production" etc.
}

const initSentry = ({ environment }: SentryCofigProps) => {
  const isLocalhost = Boolean(
    window.location.hostname === 'localhost' ||
      // [::1] is the IPv6 localhost address.
      window.location.hostname === '[::1]' ||
      // 127.0.0.1/8 is considered localhost for IPv4.
      window.location.hostname.match(
        /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
      )
  )
  // No Sentry tracking on local developments.
  if (isLocalhost) return

  // If Sentry is already initialised, return out.
  if (!!Sentry.getClient()) return

  // non-prod default config
  let replayConfig = {
    // Capture Replay for 0% of all sessions,
    // plus for 20% of sessions with an error
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.2
  }

  if (environment === 'production' || environment === 'prod') {
    // prod config
    replayConfig = {
      // Capture Replay for 5% of all sessions,
      // plus for 80% of sessions with an error
      replaysSessionSampleRate: 0.05,
      replaysOnErrorSampleRate: 0.8
    }
  }

  // RELEASE_TAG on dev envs is the GIT_BRANCH value.
  const releaseTagVersion = process.env.REACT_APP_RELEASE_TAG

  const sentryRelease = releaseTagVersion || 'LOCAL'

  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,

    release: `${sentryRelease}`,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        networkDetailAllowUrls: [
          window.location.origin,
          'https://graph.api.slerp.com.checkout.slerp.io/v1/graphql', // dev
          'https://mobile.graph.api.slerp.com/v1/graphql' // prod
        ],
        beforeAddRecordingEvent: (event) => stripPiiDataFromPayload(event)
      }),
      Sentry.httpClientIntegration(),
      Sentry.extraErrorDataIntegration({ depth: 5 })
    ],
    beforeBreadcrumb: (breadcrumb, hint) => {
      const expandedBreadcrumb = addOperationNameToBreadcrumbs(breadcrumb, hint)
      return expandedBreadcrumb
    },
    environment: environment,
    allowUrls: [`slerp.com`, `stripe.com`],
    beforeSend: (error, hint) => {
      return shouldIgnoreError(error, hint) ? null : error
    },

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    tracesSampleRate: 1.0,

    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [/^\//],
    ...replayConfig
  })
}

export { initSentry }
